/**
 * Dev-only "today" override for testing date-driven UI (the CFP timeline /
 * countdown in particular). It lets you pretend it's a different calendar day
 * without changing any data, so you can watch e.g. the CFP review step flip
 * from "scheduled" to the live "Now" state on the close date.
 *
 * Stored in a cookie scoped to the registrable domain (e.g. `.bsidesboulder.org`)
 * rather than localStorage, so the simulated date is SHARED across every
 * subdomain — set it on `dev.organize.bsidesboulder.org` and it also applies on
 * the CFP vanity host `cfp.dev.bsidesboulder.org`. localStorage is per-origin,
 * which is why a date set on the toolkit used to be invisible on the CFP host.
 *
 * Hard gate: every entry point checks `isDevHost()` first, so the override is
 * physically incapable of taking effect in production — even though the cookie's
 * parent-domain scope means a prod host like `organize.bsidesboulder.org` also
 * receives it, `getDevTodayOverride()` returns null there.
 *
 * Set it with `?devToday=2026-08-01` in the URL (or `?devToday=clear` to clear)
 * on a dev host; the value persists in the cookie until cleared.
 */

const COOKIE_KEY = 'dev-sim-today';
const LEGACY_STORAGE_KEY = 'dev-sim-today'; // pre-cookie localStorage location
const YMD_RE = /^\d{4}-\d{2}-\d{2}$/;
const ONE_YEAR_SECONDS = 60 * 60 * 24 * 365;

/** Mirrors DevBanner's gate: localhost, 127.0.0.1, dev.*, or *.dev.* hosts. */
export function isDevHost(): boolean {
  if (typeof window === 'undefined') return false;
  const host = window.location.hostname;
  return (
    host === 'localhost' ||
    host === '127.0.0.1' ||
    host.startsWith('dev.') ||
    host.includes('.dev.')
  );
}

/**
 * The domain to scope the cookie to so every subdomain shares it. Returns the
 * registrable parent (last two labels, e.g. `.bsidesboulder.org`) for normal
 * hostnames, or null for `localhost`/IP literals where a host-only cookie is
 * the only option.
 */
function cookieDomain(): string | null {
  const host = window.location.hostname;
  if (host === 'localhost' || host === '127.0.0.1' || /^[\d.]+$/.test(host)) return null;
  const parts = host.split('.');
  if (parts.length < 2) return null;
  return `.${parts.slice(-2).join('.')}`;
}

function readCookie(): string | null {
  try {
    const match = document.cookie.match(new RegExp(`(?:^|; )${COOKIE_KEY}=([^;]*)`));
    return match ? decodeURIComponent(match[1]) : null;
  } catch {
    return null;
  }
}

function writeCookie(value: string | null): void {
  const domain = cookieDomain();
  const domainAttr = domain ? `; Domain=${domain}` : '';
  // `Secure` is required for cross-site contexts but is only honored over https;
  // omit it on localhost (http) or the cookie would be silently dropped.
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  if (value) {
    document.cookie = `${COOKIE_KEY}=${encodeURIComponent(value)}; Path=/${domainAttr}; Max-Age=${ONE_YEAR_SECONDS}; SameSite=Lax${secure}`;
  } else {
    // To delete, the Path and Domain must match the original; Max-Age=0 expires it.
    document.cookie = `${COOKIE_KEY}=; Path=/${domainAttr}; Max-Age=0; SameSite=Lax${secure}`;
  }
}

/**
 * The simulated "today" (YYYY-MM-DD) when set on a dev host, otherwise null.
 * Never returns a value in production. One-time migrates a legacy localStorage
 * value into the shared cookie so an in-flight override isn't lost on upgrade.
 */
export function getDevTodayOverride(): string | null {
  if (!isDevHost()) return null;
  const fromCookie = readCookie();
  if (fromCookie && YMD_RE.test(fromCookie)) return fromCookie;
  // Migrate a pre-cookie value (only visible on the origin that set it).
  try {
    const legacy = window.localStorage.getItem(LEGACY_STORAGE_KEY);
    if (legacy && YMD_RE.test(legacy)) {
      writeCookie(legacy);
      window.localStorage.removeItem(LEGACY_STORAGE_KEY);
      return legacy;
    }
  } catch {
    /* localStorage unavailable — ignore */
  }
  return null;
}

/** Set (valid YYYY-MM-DD) or clear (null/invalid) the simulated date. No-op in prod. */
export function setDevTodayOverride(ymd: string | null): void {
  if (!isDevHost()) return;
  writeCookie(ymd && YMD_RE.test(ymd) ? ymd : null);
  // Clear any stale per-origin legacy value so it can't shadow the cookie.
  try {
    window.localStorage.removeItem(LEGACY_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * One-time bootstrap from the URL: `?devToday=2026-08-01` sets the simulated
 * date; `?devToday=clear` (or empty) clears it. Dev hosts only. Strips the
 * param afterward so it doesn't linger in the address bar or get shared.
 */
export function initDevTodayFromUrl(): void {
  if (!isDevHost()) return;
  try {
    const url = new URL(window.location.href);
    if (!url.searchParams.has('devToday')) return;
    const raw = url.searchParams.get('devToday') ?? '';
    setDevTodayOverride(raw === '' || raw === 'clear' ? null : raw);
    url.searchParams.delete('devToday');
    window.history.replaceState({}, '', url.toString());
  } catch {
    /* malformed URL — ignore */
  }
}
